import { Component, inject, Injector, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Context, DataType, RuleInterface } from '@vendasta/automata';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { nonNegativeIntegerValidator } from '../../../common/utils';
import { SmbUsersSelectorService } from '../../../../../data-services/smb-users-selector.service';
import {
  AutomationReplacementChipsComponent,
  AutomationVariableMenuComponent,
  insertVariable,
} from '../../../../../common/components';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { CrmFieldOptionsSelectorService } from '../../../../../data-services/crm-field-options-selector.service';
import { StandardIds } from '@galaxy/crm/static';
import { AutomataContextService } from '@galaxy/automata/shared';
import { SalespersonSelectorService } from '../../../../../data-services/salesperson-selector-service';
import { ASSIGNED_SALESPERSON } from '../../../../../data-services/users.service';

@Component({
  selector: 'automata-create-crm-note-panel',
  templateUrl: './crm-task-create-panel.component.html',
  styleUrls: ['./crm-task-create-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationVariableMenuComponent,
    GalaxyInfiniteScrollTriggerModule,
    GalaxyTooltipModule,
    AutomationReplacementChipsComponent,
  ],
})
export class CrmTaskCreatePanelComponent extends CommonActionPanelComponent implements OnInit {
  supportedDataTypes: DataType[] = [DataType.DATA_TYPE_SMB_USER_ID, DataType.DATA_TYPE_STRING];
  tagsService = inject(CrmFieldOptionsSelectorService);
  public activityTags = StandardIds.ActivityTags;

  form: UntypedFormGroup = new UntypedFormGroup({
    title: new FormControl<string>('', Validators.required),
    body: new FormControl<string>('', Validators.required),
    tags: new FormControl<string[]>([]),
    salesperson: new FormControl<string>(ASSIGNED_SALESPERSON, Validators.required),
  });

  public automationContext = this.automationContextService.currentContext;

  isSMB = this.automationContext === Context.AUTOMATION_CONTEXT_SMB;

  priorities = [
    { value: 'High', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_PRIORITY.OPTIONS.HIGH' },
    { value: 'Medium', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_PRIORITY.OPTIONS.MEDIUM' },
    { value: 'Low', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_PRIORITY.OPTIONS.LOW' },
  ];

  taskTypes = [
    { value: 'Email', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_TYPE.OPTIONS.EMAIL' },
    { value: 'Call', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_TYPE.OPTIONS.CALL' },
    { value: 'Message', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_TYPE.OPTIONS.MESSAGE' },
    { value: 'Connection', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_TYPE.OPTIONS.CONNECTION' },
    { value: 'To-do', label: 'AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.TASK_TYPE.OPTIONS.TO-DO' },
  ];

  constructor(
    private readonly automationContextService: AutomataContextService,
    private injector: Injector,
    protected readonly smbUsersSelectorService: SmbUsersSelectorService,
  ) {
    super();
  }

  salespersonSelectorService = this.injector.get(SalespersonSelectorService);

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step?.data ? JSON.parse(this.step!.data!) : {};
    this.form.addControl(
      'task_days_to_complete',
      new UntypedFormControl(jsonData?.task_days_to_complete || 0, [Validators.required, nonNegativeIntegerValidator]),
    );
    this.form.addControl('task_priority', new UntypedFormControl(jsonData?.task_priority || ''));
    this.form.addControl('task_type', new UntypedFormControl(jsonData?.task_type || ''));
    this.form.controls.title.setValue(jsonData?.task_name || '');
    this.form.controls.body.setValue(jsonData?.task_body || '');
    this.form.controls.tags.setValue(jsonData?.tags || []);
    this.form.controls.salesperson.setValue(jsonData?.salesperson_id || ASSIGNED_SALESPERSON);
  }

  finishedLoadingSelector(): void {
    this.savingEnabled = true;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step?.data ? JSON.parse(this.step!.data!) : {};

    jsonData.task_name = this.form.controls.title.value;
    jsonData.task_body = this.form.controls.body.value;
    jsonData.task_days_to_complete = this.form.controls.task_days_to_complete.value;
    jsonData.task_priority = this.form.controls.task_priority.value;
    jsonData.tags = this.form.controls.tags.value;
    jsonData.salesperson_id = this.form.controls.salesperson?.value;
    jsonData.task_type = this.form.controls.task_type.value;

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  insertVariable(elementID: string, control: AbstractControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }
}
